import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { api } from '../dados/api.js'

/// Limpeza dos cadastros que a migração criou por engano.
///
/// Dois passos, sempre. O primeiro conta e mostra; o segundo executa repetindo o
/// número contado. Se o banco mudar entre um e outro, o servidor recusa e nada
/// acontece — apagar milhares de linhas é o tipo de coisa que não pode depender de
/// um clique estar no lugar certo.

export function LimpezaCadastros() {
  const cliente = useQueryClient()
  const [aberto, setAberto] = useState(false)
  const [motivo, setMotivo] = useState('')
  const [feito, setFeito] = useState<string | null>(null)

  const simulacao = useMutation({
    mutationFn: () => api.adminSimularLimpeza(),
  })

  const limpeza = useMutation({
    mutationFn: (total: number) => api.adminLimpar(total, motivo.trim() || undefined),
    onSuccess: (r) => {
      setFeito(`${r.total.toLocaleString('pt-BR')} cadastros excluídos.`)
      simulacao.reset()
      setAberto(false)
      setMotivo('')
      void cliente.invalidateQueries({ queryKey: ['admin'] })
    },
  })

  if (feito) {
    return (
      <div className="cartao mb-4 border-leve/30 bg-leve/5 p-3">
        <p className="text-sm font-semibold text-leve">{feito}</p>
        <p className="mt-1 text-xs text-slate-600">O registro ficou na aba Auditoria.</p>
      </div>
    )
  }

  if (!aberto) {
    return (
      <button
        onClick={() => {
          setAberto(true)
          simulacao.mutate()
        }}
        className="botao-neutro mb-4 w-full !min-h-0 !py-2 text-sm"
      >
        Limpar cadastros sem uso…
      </button>
    )
  }

  const erro = (simulacao.error ?? limpeza.error) as Error | null

  return (
    <div className="cartao mb-4 border-moderada/30 bg-moderada/5 p-3">
      <p className="font-semibold text-slate-800">Limpar cadastros sem uso</p>

      {simulacao.isPending && <p className="mt-1 text-sm text-slate-600">Conferindo…</p>}

      {erro && (
        <p className="mt-2 rounded bg-alta/10 px-3 py-2 text-sm text-alta" role="alert">
          {erro.message}
        </p>
      )}

      {simulacao.data && (
        <>
          {simulacao.data.total === 0 ? (
            <p className="mt-1 text-sm text-slate-600">
              Nenhum cadastro se encaixa. Não há nada para limpar.
            </p>
          ) : (
            <>
              <p className="mt-1 text-sm text-slate-700">
                <strong>{simulacao.data.total.toLocaleString('pt-BR')} cadastros</strong> sem acesso,
                sem perfil, que nunca entraram e sem nenhuma inspeção ou registro ligado a eles.
              </p>
              <p className="mt-2 text-xs text-slate-600">{simulacao.data.aviso}</p>

              <details className="mt-2">
                <summary className="cursor-pointer text-xs font-medium text-marinho">
                  Ver os 20 primeiros
                </summary>
                <ul className="mt-1 max-h-40 overflow-y-auto text-xs text-slate-600">
                  {simulacao.data.amostra.map((p) => (
                    <li key={p.id}>
                      {p.nome} · {p.email}
                    </li>
                  ))}
                </ul>
              </details>

              <label className="mt-3 block">
                <span className="text-xs font-semibold text-slate-600">Motivo (fica no registro)</span>
                <input
                  value={motivo}
                  onChange={(e) => setMotivo(e.target.value)}
                  placeholder="Ex.: limpeza dos vínculos importados do RM"
                  className="campo mt-1"
                />
              </label>
            </>
          )}
        </>
      )}

      <div className="mt-3 flex gap-2">
        {simulacao.data && simulacao.data.total > 0 && (
          <button
            onClick={() => limpeza.mutate(simulacao.data.total)}
            disabled={limpeza.isPending}
            className="botao-perigo flex-1"
          >
            {limpeza.isPending
              ? 'Excluindo…'
              : `Excluir ${simulacao.data.total.toLocaleString('pt-BR')}`}
          </button>
        )}
        <button
          onClick={() => {
            setAberto(false)
            simulacao.reset()
            limpeza.reset()
            setMotivo('')
          }}
          className="botao-neutro flex-1"
        >
          Cancelar
        </button>
      </div>
    </div>
  )
}
